"use client";

import React from "react";
import {Box, Typography, Link, alpha} from "@mui/material";
import Image from "next/image";
import { stripTags } from "@/utils/content-utils";

type Expert = {
  title: string;
  description: string;
  imgUrl: string;
  linkUrl?: string;
  linkText?: string;
};

interface ExpertCardProps {
  expert: Expert;
  cardIndex?: number;
}

const ExpertCard: React.FC<ExpertCardProps> = ({ expert, cardIndex }) => {
  const cardId = cardIndex !== undefined ? `homepage-expert-section-card-${cardIndex}` : "homepage-expert-section-card";
  return (
    <Box
      data-testid={cardId}
      data-card-name={expert.title}
      data-carousel-position={cardIndex !== undefined ? String(cardIndex + 1) : undefined}
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "20px",
        overflow: "hidden",
        backgroundColor: "background.warm_strong",
        boxShadow: 6,
        display: "flex",
        flexDirection: "column",
        "::before": {
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: (theme) => `linear-gradient(
          to top,
          ${alpha(theme.palette.background.warm_strong, 1)} 0%,
          ${alpha(theme.palette.background.warm_strong, 0.9)} 45%,
          ${alpha(theme.palette.background.warm_strong, 0.8)} 75%,
          ${alpha(theme.palette.background.warm_strong, 0)} 85%
      ),
      url("https://i.gocollette.com/img/backgrounds/pattern.svg")`,
          backgroundSize: { xs: "250% 150%", sm: "cover" },
          backgroundPosition: { xs: "center, center", sm: "top right" },
          backgroundRepeat: "no-repeat",
          opacity: 0.3,
          zIndex: 1
        },
        "& > *": {
          position: "relative",
          zIndex: 2
        }
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: { xs: "-10%", sm:"-3%", md: "-10%", lg: "-4%" },
          top: { xs: "-10%", sm:"-20%", md: "-10%", lg: "-12%"},
          width: "175px",
          height: "175px",
          borderRadius: "50%",
          border: "4px solid var(--theme-palette-info-dark)",
          overflow: "hidden",
          zIndex: 2,
          display: { xs: "none", sm: "block" }           
        }}
      >
        <Image
          src={expert.imgUrl}
          alt="Expert background"
          width={175}
          height={175}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
          data-testid={`${cardId}-image`}
        />
      </Box>
      <Box
        padding="0 32px 32px"
        marginTop={{ xs: "32px", md: "124px", lg:"104px" }}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Typography
          width={{ xs: "100%", sm: "66%" }}
          maxWidth={{xs:"327px", sm:"370px", md: "600px", lg: "270px", xl:"300px"}}
          variant="h4"
          sx={{
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            textWrap: "wrap",
            overflow: "hidden",
            "@media (min-width:700px) and (max-width:899px)": {
              maxWidth: "500px"
            }
          }}
          data-testid={`${cardId}-title`}
        >
          {expert.title}
        </Typography>
        <Typography
          lineHeight={1.5}
          variant="subtitle_lg"
          marginTop="10px"
          fontWeight={500}
          fontSize="18px"
          width="100%"
          sx={{ wordBreak: "break-word", padding:{xs: 0, sm:"0 170px 0 0", md:0} }}
          data-testid={`${cardId}-description`}
        >
          {stripTags(expert.description)}
        </Typography>
        {expert.linkUrl && expert.linkText && (
          <Link
            href={expert.linkUrl}
            sx={{
              display: "inline-block",
              marginTop: "auto",
              paddingTop: "16px",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              fontSize: "16px",
              color: "secondary.main",
              textDecoration: "underline",
              textTransform: "uppercase",
              "&:hover": {
                color: "secondary.dark"
              }
            }}
            data-testid={`${cardId}-link`}
          >
            {expert.linkText}
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default ExpertCard;